export function RisksAndMitigation() {
  return (
    <section className="py-20 px-4 bg-white">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Understanding Risks and How to Mitigate Them
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            While seller financing offers tremendous benefits, it's important to understand potential risks and implement strategies to protect your investment
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center mr-4">
                <i className="ri-alert-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900">Potential Risks</h3>
            </div>
            
            <div className="space-y-6">
              <div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">Buyer Default Risk</h4>
                <p className="text-gray-700 leading-relaxed">
                  The buyer may stop making payments, requiring you to initiate foreclosure proceedings and potentially regain the property.
                </p>
              </div>

              <div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">Property Deterioration</h4>
                <p className="text-gray-700 leading-relaxed">
                  Buyer may not maintain the property properly, potentially reducing its value if you need to foreclose.
                </p>
              </div>

              <div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">Opportunity Cost</h4>
                <p className="text-gray-700 leading-relaxed">
                  Capital is tied up in the loan rather than available for other investments or opportunities.
                </p>
              </div>

              <div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">Market Value Decline</h4>
                <p className="text-gray-700 leading-relaxed">
                  If real estate values decline significantly, the property may be worth less than the outstanding loan balance.
                </p>
              </div>

              <div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">Inflation Risk</h4>
                <p className="text-gray-700 leading-relaxed">
                  Fixed-rate loans mean you receive the same payment amount even as inflation reduces purchasing power over time.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center mr-4">
                <i className="ri-shield-check-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900">Risk Mitigation Strategies</h3>
            </div>
            
            <div className="space-y-6">
              <div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">Thorough Buyer Screening</h4>
                <p className="text-gray-700 leading-relaxed">
                  Verify buyer's financial strength, experience, credit history, and track record. Request financial statements and references.
                </p>
              </div>

              <div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">Substantial Down Payment</h4>
                <p className="text-gray-700 leading-relaxed">
                  Require 20-30% down payment to ensure buyer has significant equity and motivation to continue making payments.
                </p>
              </div>

              <div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">Property Insurance Requirements</h4>
                <p className="text-gray-700 leading-relaxed">
                  Mandate comprehensive property insurance with you named as loss payee to protect against damage or loss.
                </p>
              </div>

              <div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">Regular Payment Monitoring</h4>
                <p className="text-gray-700 leading-relaxed">
                  Track payments closely and address any late payments immediately before they become major problems.
                </p>
              </div>

              <div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">Balloon Payment Structure</h4>
                <p className="text-gray-700 leading-relaxed">
                  Use shorter loan terms with balloon payments (5-10 years) to reduce long-term exposure and risk.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 rounded-lg p-8 md:p-12 mb-12">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Essential Protective Measures
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg p-6 shadow-md">
              <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-file-shield-line text-3xl text-white"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3 text-center">Professional Documentation</h4>
              <p className="text-gray-600 text-center leading-relaxed">
                Use experienced real estate attorneys to draft comprehensive promissory notes, mortgages, and security agreements
              </p>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-md">
              <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-search-eye-line text-3xl text-white"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3 text-center">Due Diligence on Buyer</h4>
              <p className="text-gray-600 text-center leading-relaxed">
                Conduct thorough background checks, verify financial capacity, and review buyer's real estate investment experience
              </p>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-md">
              <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-home-gear-line text-3xl text-white"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3 text-center">Property Maintenance Clauses</h4>
              <p className="text-gray-600 text-center leading-relaxed">
                Include contract provisions requiring buyer to maintain property in good condition and allow periodic inspections
              </p>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-md">
              <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-user-line text-3xl text-white"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3 text-center">Title Insurance</h4>
              <p className="text-gray-600 text-center leading-relaxed">
                Obtain title insurance to protect against title defects, liens, or other issues that could affect your security interest
              </p>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-md">
              <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-calendar-check-line text-3xl text-white"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3 text-center">Escrow Services</h4>
              <p className="text-gray-600 text-center leading-relaxed">
                Use professional loan servicing companies to collect payments, track balances, and handle tax reporting
              </p>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-md">
              <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-alert-line text-3xl text-white"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3 text-center">Default Provisions</h4>
              <p className="text-gray-600 text-center leading-relaxed">
                Clearly define what constitutes default and outline specific remedies and foreclosure procedures in the contract
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <img
              src="https://public.readdy.ai/ai/img_res/39d2ace7a22f3bf91a981d480fef2989.jpg"
              alt="Risk mitigation strategies for seller financing"
              className="w-full h-96 object-cover object-top rounded-lg shadow-lg"
            />
          </div>

          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">
              The Reality: Risks Are Manageable
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              While seller financing does involve risks, they are <strong>highly manageable</strong> with proper planning and protective measures. The key is to approach seller financing as a serious business transaction, not a casual arrangement.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Most seller financing arrangements are successful when sellers properly screen buyers, require substantial down payments, use professional legal documentation, and maintain appropriate oversight. The foreclosure rate for seller-financed properties is actually quite low when these best practices are followed.
            </p>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              Remember that even in a worst-case foreclosure scenario, you're not losing money—you keep all payments received, regain a valuable property that likely appreciated, and can resell it again. This built-in protection makes seller financing significantly safer than many other investment options.
            </p>

            <div className="bg-teal-50 border-l-4 border-teal-600 p-6 rounded-md">
              <h4 className="text-xl font-bold text-gray-900 mb-3">Professional Guidance</h4>
              <p className="text-gray-700 leading-relaxed">
                Always work with experienced real estate attorneys, tax advisors, and financial professionals when structuring seller financing arrangements. Their expertise helps you avoid pitfalls and maximize the benefits while minimizing risks.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}